<!-- eslint-disable no-new -->
<script setup>
import { ref, computed, onBeforeMount } from "vue";

const props = defineProps({
	authorName: { type: String, required: true },
	authorTitle: { type: String, default: "" },
	authorExpertise: { type: String, default: "" },
	authorEducation: { type: String, default: "" },
	authorHighlights: { type: Array, default: () => [] },
	authorExperience: { type: String, default: "" },
	authorEmail: { type: String, default: "" },
	authorImage: { type: String, default: "" },
	authorPosts: { type: String, default: "", required: false },
	anyObject: { type: Object, default: () => {} }
});

const subtitle = ref("");
const videoContainer = ref(null);

const posts = ref([]);
const visiblePostCount = ref(8);

const PUBLICATION_AND_INTERVIEWS_LABELS = [
	"Interviews",
	"Featured on",
	"Host",
	"Author",
	"Founder"
];

const linkfy = (text) => {
	const regex =
		/(\bTimothySykes\.com\b|\bTimothysykes\.com\b|\bStocksToTrade\.com\b|\bStocksToTrade\b|\bTrading Challenge\b|\bAn American Hedge Fund: How I Made \$2 Million as a Stock Operator & Created a Hedge Fund\b|\bAn American Hedge Fund\b|\$7\.4 million|\b31 millionaire students\b|\bKarmagawa\b|\bSteadyTrade Team\b|\bdip and rip\b|\b9:45 rule\b)/g;
	const parts = text.split(regex);

	const elements = parts.map((part) => {
		if (/\bStocksToTrade\.com\b/.test(part) || /\bStocksToTrade\b/.test(part)) {
			return `<a class="blueLink" href="https://www.stockstotrade.com" target="_blank">${part}</a>`;
		} else if (
			/\bTimothySykes\.com\b/.test(part) ||
			/\bTimothysykes\.com\b/.test(part)
		) {
			return `<a class="blueLink" href="https://www.timothysykes.com/" target="_blank">${part}</a>`;
		} else if (/\bTrading Challenge\b/.test(part)) {
			return `<a class="blueLink" href="https://www.timothysykes.com/millionaire-challenge/?utm_source=blog&utm_medium=popup&utm_campaign=Qhdzor" target="_blank">${part}</a>`;
		} else if (
			/\bAn American Hedge Fund: How I Made \$2 Million as a Stock Operator & Created a Hedge Fund\b/.test(
				part
			) ||
			/\bAn American Hedge Fund\b/.test(part)
		) {
			return `<a class="blueLink" href="https://timsykesbook.com/?utm_source=organic&utm_medium=blog_post&utm_campaign=sykes-blog&utm_content=newbie-traders" target="_blank">${part}</a>`;
		} else if (/\$7\.4 million/.test(part)) {
			return `<a class="blueLink" href="https://profit.ly/user/timothysykes/chart" target="_blank">${part}</a>`;
		} else if (/\b31 millionaire students\b/.test(part)) {
			return `<a class="blueLink" href="https://www.timothysykes.com/blog/how-many-millionaire-students/" target="_blank">${part}</a>`;
		} else if (/\bKarmagawa\b/.test(part)) {
			return `<a class="blueLink" href="https://karmagawa.com/" target="_blank">${part}</a>`;
		} else if (/\bSteadyTrade Team\b/.test(part)) {
			return `<a class="blueLink" href="https://stockstotrade.com/steadytrade/" target="_blank">${part}</a>`;
		} else if (/\bdip and rip\b/.test(part)) {
			return `<a class="blueLink" href="https://stockstotrade.com/dip-and-rip-pattern/" target="_blank">${part}</a>`;
		} else if (/\b9:45 rule\b/.test(part)) {
			return `<a class="blueLink" href="https://www.youtube.com/watch?v=PX7cqP_6a8A" target="_blank">${part}</a>`;
		} else {
			return `<span class="text">${part}</span>`;
		}
	});
	return elements.join("");
};

const experience = computed(() => {
	if (props.anyObject.experience) {
		return props.anyObject.experience.replace(/"/g, "");
	}

	return "";
});
const expertise = computed(() => {
	if (props.authorExpertise) {
		return props.authorExpertise.replace(/"/g, "");
	}

	return "";
});
const education = computed(() => {
	if (props.authorEducation) {
		return props.authorEducation.replace(/"/g, "");
	}

	return "";
});

const highlights = computed(() => {
	if (props.authorHighlights) {
		return props.authorHighlights.replace(/"/g, "");
	}
	return "";
});
const sections = computed(() => {
	if (props.anyObject.custom_sections) {
		return JSON.parse(props.anyObject.custom_sections);
	}
	return [];
});

async function onYouTubeIframeAPIReady(id) {
	try {
		if (
			!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')
		) {
			const script = document.createElement("script");
			script.setAttribute("src", "https://www.youtube.com/iframe_api");
			document.head.appendChild(script);
		}

		await setTimeout(() => {
			new YT.Player("youtube-player", {
				videoId: id, // Replace with your video's ID
				playerVars: {
					autoplay: true, // Autoplay is turned off
					controls: 1, // Show video controls
					modestbranding: 1, // Hide the YouTube logo
					rel: 0 // Don't show related videos
				}
			});
		}, 2000);
	} catch (e) {
		 
		console.log("Error in Video injection: ", e);
	}
}
watch(videoContainer, (video) => {
	if (import.meta.client && video && video.length > 0) {
		const id = video[0].getAttribute("data-url").split("v=")[1];
		onYouTubeIframeAPIReady(id);
	}
});
onBeforeMount(() => {
	document.title = `StocksToTrade - ${props.authorName}`;
	if (props.authorTitle) {
		subtitle.value = linkfy(props.authorTitle);
	}
});
</script>

<template>
	<div class="author-card-container">
		<div class="author-card">
			<div class="author-image">
				<img
					v-if="authorImage"
					:src="authorImage"
					alt="Author Headshot"
				/>
				<img
					v-else
					src="https://secure.gravatar.com/avatar/04554fdd4796f793a329464e8d983881?s=120&d=mm&r=g"
					alt="Author Headshot"
				/>
			</div>
			<div class="author-information">
				<div class="author-details">
					<span class="author-name white title">{{ authorName }}</span>
					<span
						class="text-white"
						v-html="
							linkfy(anyObject.position != '' ? anyObject.position : authorTitle)
						"
					/>
				</div>

				<hr class="separator" />
				<div class="author-proficiency">
					<div class="author-expertise">
						<span class="author-proficiency-title blueish-color">Expertise</span>
						<span
							class="author-expertise-description white text-white"
							v-html="expertise"
						></span>
					</div>
					<div class="author-education">
						<span class="author-proficiency-title blueish-color">Education</span>
						<span
							class="author-expertise-description white text-white"
							v-html="linkfy(education)"
						/>
					</div>
				</div>
			</div>
			<div class="author-socialmedia">
				<a
					v-if="authorEmail"
					:href="'mailto:' + authorEmail"
					class="social-icon email-social"
					target="_blank"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="5"
						height="5"
						viewBox="0 0 16 16"
					>
						<path
							fill="#fff"
							d="M15,1H1C0.448,1,0,1.447,0,2v12c0,0.553,0.448,1,1,1h14c0.552,0,1-0.447,1-1V2C16,1.447,15.552,1,15,1z 
						M14,13H2V6.723l5.504,3.145c0.308,0.176,0.685,0.176,0.992,0L14,6.723V13z M14,4.42L8,7.849L2,4.42V3h12V4.42z"
						/>
					</svg>
				</a>

				<template
					v-if="
						sections.length > 0 &&
						sections.filter((s) => ['Social'].includes(s.name)).length > 0
					"
				>
					<template v-for="section in sections">
						<template v-if="section.name === 'Social'">
							<NuxtLink
								v-for="(item, index) in section.values"
								:key="index"
								:to="item.value"
								:href="item.value"
								class="social-icon"
								target="_blank"
							>
								<img
									class="w-6 h-6 object-cover"
									:src="`/images/icons/${
										item.label === 'profitly'
											? 'profitly.png'
											: `${item.label.toLowerCase()}.svg`
									}`"
								/>
							</NuxtLink>
						</template>
					</template>
				</template>
			</div>
		</div>
	</div>

	<div
		id="author-page"
		class="custom-link"
	>
		<BlogContainer class="custom-blog-container bg-white">
			<template #aside>
				<div class="lead-capture">
					<div class="gradient-title !text-[28px] text-center !leading-normal black">
						Get Daily, FREE Trade Alerts From Our Algorithm
					</div>
					<div class="lead-capture-image">
						<img
							src="/images/lead-capture.png"
							alt=""
						/>
					</div>
					<div>
						<input
							type="text"
							class="form-control mb-[16px]"
							placeholder="Email"
							id="email"
							name="email"
						/>

						<CtaButton
							type="secondary"
							class="justify-center w-full"
						>
							Reserve Your Spot Now
						</CtaButton>
					</div>
				</div>
				<div
					class="related-post-container"
					v-if="authorPosts"
				>
					<AuthorRelatedPosts
						:posts="authorPosts"
						:apply-custom-style="true"
					/>
				</div>
			</template>

			<template #main>
				<template
					v-if="
						sections.length > 0 &&
						sections.filter((s) => ['Video'].includes(s.name)).length > 0
					"
				>
					<template v-for="section in sections">
						<template v-if="section.name && ['Video'].includes(section.name)">
							<div
								v-for="(item, index) in section.values"
								:key="index"
								id="youtube-player"
								ref="videoContainer"
								class="w-full max-w-[700px] aspect-video mb-6"
								:data-url="item.value"
							></div>
						</template>
					</template>
				</template>

				<div class="author-highlights mb-8">
					<h2 class="author-content-title">Highlights</h2>

					<div v-html="highlights"></div>
				</div>
				<div
					v-if="experience"
					class="author-body mb-8"
				>
					<h2 class="author-content-title">Experience</h2>
					<div
						v-html="experience"
						class="author-experience-paragraph"
					></div>
				</div>

				<div
					v-if="
						sections.length > 0 &&
						sections.filter((s) =>
							['Interviews', 'Featured on', 'Host', 'Author', 'Founder'].includes(
								s.name
							)
						).length > 0
					"
				>
					<h2 class="author-content-title pb-0">Publications and Interviews</h2>
					<template v-for="section in sections">
						<div
							v-if="
								section.name && PUBLICATION_AND_INTERVIEWS_LABELS.includes(section.name)
							"
							:key="section.name"
							class="flex flex-col mb-6"
						>
							<h3 class="text-lg font-semibold !mb-2">{{ section.name }}</h3>

							<div class="grid lg:grid-cols-2 row-gap-2">
								<span
									v-for="(item, index) in section.values"
									:key="index"
								>
									<NuxtLink
										:to="item.value"
										target="_blank"
										class="author-link !text-blue-300 !hover:text-blue-500"
									>
										{{ item.label }}
									</NuxtLink>
								</span>
							</div>
						</div>
					</template>
				</div>
			</template>
		</BlogContainer>
	</div>
</template>

<style lang="scss" scoped>
@use "~/assets/utils.scss" as *;

#custom-padding {
	padding: 0;

	span,
	p,
	h2 {
		font-family: "Open Sauce Sans", sans-serif;
	}
}
.author-card-container {
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 32px 382px;
	background: linear-gradient(110deg, #091d56 8.05%, #331290 77.58%);
}

.title {
	font-weight: 700;
	font-size: 24px;
}
.subtitle {
	font-weight: 400;
	font-size: 16px;
	color: white !important;
}

.author-card {
	display: grid;
	width: 920px;
	grid-template-columns: auto 1fr;
	grid-gap: 20px;
	grid-template-rows: auto auto;
	row-gap: 16px;
	align-items: center;
}
.author-name {
	display: block;
	letter-spacing: -2px;
	font-family: "Open Sauce Sans", sans-serif;
	font-weight: 500;
	font-size: 24px;
	color: white;
}
.author-title {
	display: block;
	font-size: 16px;
	font-weight: 400;
	color: #fac515;
}
.white {
	color: white;
}

.blueish-color {
	color: #3fa9f5 !important;
}

:deep(.blueLink) {
	color: #3fa9f5 !important;
}

:deep(.blueLink:hover) {
	color: #f78400;
	text-decoration: underline;
	text-decoration-color: white;
}

:deep(.subtitle a) {
	color: #3fa9f5;
	text-decoration: none;
	text-decoration-color: white;
}

.author-post-container {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	width: 1200px;
	gap: 32px;
	margin-top: 16px;
	margin-bottom: 20px;
}
.author-proficiency-title {
	font-size: 18px;
	font-weight: 700;
}
.author-image {
	display: inline-block;
	border-radius: 200px;
	border: 5px solid #3fa9f5;
	overflow: hidden;
	width: 160px;
	height: 160px;
	margin-right: 20px;
	align-self: start;
}

.author-image img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.author-information {
	flex: 1;
	display: flex;
	flex-direction: column;
}

:deep(.separator) {
	color: white;
	box-shadow: inset 0 0.25px 0 0 #fff;
	margin: 16px 0;
}

.post-separator {
	margin-top: 52px;
	margin-bottom: 32px;
}

.author-expertise-description {
	padding: 8px 0;
	font-size: 18px;
}

.author-expertise-description,
.author-education-description,
.author-details,
.author-post-description span {
	display: block;
}

.author-details {
	width: 100%;
	margin-bottom: 16px;
}
.author-socialmedia {
	grid-column: 2 / span 1;
	grid-row: 2 / span 1;
}

.view-more-button-container {
	display: flex;
	justify-content: center;
	align-items: center;
	text-align: center;
	padding: 20px;
}

.view-more-button {
	-webkit-box-align: center;
	align-items: center;
	cursor: pointer;
	display: inline-flex;
	-webkit-box-pack: center;
	justify-content: center;
	pointer-events: auto;
	position: relative;
	text-align: center;
	transition: all 175ms ease 0s;
	user-select: none;
	white-space: nowrap;
	background: #1e3a8a;
	box-sizing: border-box;
	font-size: 16px;
	font-family: Rubik, sans-serif;
	font-weight: 500;
	line-height: 1;
	padding: 12px 20px;
	height: min-content;
	border-radius: 8px;
	border: 0px solid transparent;
	box-shadow:
		rgb(28, 80, 158) 0px -1px 0px 0px inset,
		rgba(0, 0, 0, 0.12) 0px 2px 3px 0px;
	color: rgb(255, 255, 255);
	min-height: 48px;
	min-width: 48px;
	margin: initial;
}

/*Social media Icos*/
.social-icon {
	display: inline-flex;
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-color: #3fa9f5;
	text-align: center;
	line-height: 40px;
	overflow: hidden;
	margin-right: 26px;
	transition: background-color 0.3s ease;
	align-items: center;
	justify-content: center;
	z-index: 1000;
}

.social-icon svg {
	display: block;
	margin: 0 auto;
	width: 60%;
	height: 60%;
	object-fit: contain;
	transition: transform 0.3s ease;
}

.social-icon:hover {
	background-color: #759cc4;
}

.social-icon:hover img {
	transform: scale(1.1);
}

.author-highlights,
.author-experience,
.author-education {
	grid-column: 2;
}

.author-content-title {
	font-size: 38px;
	font-weight: 700;
	margin-bottom: 32px;
}

.author-highlight-item,
.author-experience-paragraph,
.author-education-paragraph {
	font-weight: 400;
	font-size: 18px;
	text-align: justify;
	margin-bottom: 24px;
}

.author-highlight-list {
	list-style-type: none;
	padding-left: 8px;
}

.author-highlight-list li {
	position: relative;
	padding-left: 16px;
	line-height: 1.4;
}

.author-highlight-list li::before {
	content: "";
	position: absolute;
	left: 0;
	top: 8px;
	width: 8px;
	height: 8px;
	background-color: #3094fb;
}

.author-latest-posts {
	grid-column: 2 / span 1;
	grid-row: 4;
	margin-left: 100px;
	margin-right: 100px;
	margin-bottom: 60px;
}

.author-content-title-post {
	font-size: 38px;
	font-weight: 700;
	padding-bottom: 0px;
}

.author-side-box {
	margin-left: 80px;
	grid-column: 4 / span 1;
	grid-row: 1 / span 2;
	width: 380px;
	height: 548.07px;
	border-radius: 24px;
	background-color: white;
	display: flex;
	flex-direction: column;
	padding: 20px;
	gap: 20px;
	box-shadow: 0px 7px 20px 0px #0000001a;
}

.author-watchlist-subscription-ad {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.author-watchlist-image {
	width: 100%;
}

.author-watchlist-image img {
	width: 100%;
	height: auto;
	object-fit: cover;
	border-radius: 4px;
}

.author-watchlist-information-title {
	padding-top: 24px;
	padding-bottom: 0px;
	font-size: 22px;
	font-weight: 700;
	line-height: 29px;
	letter-spacing: 0em;
	text-align: left;
	font-family: Rubik !important;
}

.author-watchlist-information-subtitle {
	padding-top: 8px 0px;
	font-size: 14px;
	font-weight: 400;
	line-height: 24px;
	letter-spacing: 0em;
	text-align: left;
	color: #1e293b;
	font-family: Rubik !important;
	display: inline-block;
}

.author-watchlist-information-email-input {
	width: 100%;
	margin-bottom: 8px;
	padding: 10px;
	border: 1px solid #cbd5e1;
	border-radius: 4px;
}

.author-watchlist-information-email-input::placeholder {
	color: #cbd5e1;
	text-align: center;
}

.author-watchlist-information-subscribe-button {
	background-color: #ef6820;
	color: #fff;
	font-weight: 700;
	font-size: 16px;
	border: none;
	border-radius: 8px;
	padding: 10px;
	font-size: 16px;
	cursor: pointer;
	position: relative;
	transition: background-color 0.3s ease;
	width: 100%;
	font-family: Rubik !important;
}

.author-watchlist-information-subscribe-button::after {
	content: "\2192";
	font-size: 32px;
	font-weight: 1000;
	width: 40px;
	height: 14.140625px;
	top: 5px;
	position: absolute;
}

.author-watchlist-information-subscribe-button:hover {
	background-color: #3fa9f5;
}

.author-watchlist-information-description {
	margin-top: 20px;
	display: flex;
	justify-content: center;
	align-items: center;
	font-family: Rubik !important;
	font-size: 16px;
	font-weight: 400;
	line-height: 24px;
	letter-spacing: 0em;
	text-align: center;
	color: #1e293b;
}

/* Mobile Styles */
@media (max-width: 1700px) {
	.author-post-container {
		grid-template-columns: repeat(3, minmax(0, 1fr));
		width: 100%;
	}
}
@media (max-width: 1200px) {
	.author-post-container {
		grid-template-columns: repeat(2, minmax(0, 1fr));
		width: 100%;
	}
}
@media (max-width: 700px) {
	.author-post-container {
		display: flex !important;
		flex-direction: column !important;
		width: 100%;
	}
}
@media (max-width: 768px) {
	.author-card-container {
		padding: 32px 20px;
	}

	.author-card {
		grid-template-columns: auto;
		grid-template-rows: auto auto auto;
		justify-items: center;
		text-align: center;
	}

	.author-image {
		margin-right: 0;
	}

	.author-information {
		margin-top: 20px;
	}

	.author-content-title {
		font-size: 28px;
	}

	.author-content-title-post {
		margin-left: 20px !important;
		font-size: 28px !important;
	}
	.author-highlight-list {
		padding-left: 0;
		margin-left: 0;
	}

	.author-highlight-list li::before {
		content: "";
		background-color: #3094fb;
	}

	.author-socialmedia {
		grid-column: 1;
		grid-row: 2;
		margin-top: 20px;
	}

	// .social-icon {
	// 	display: inline-block;
	// 	margin-right: 10px;
	// }
	.author-latest-posts {
		margin: 0px;
	}

	.author-post-container {
		display: block;
		gap: 24px;
		padding: 0px 20px 10px 20px;
		margin-top: 0px;
		width: 100%;
	}
	.author-post {
		grid-template-columns: 1fr;
		flex: 1;
	}
	.author-post-image {
		grid-row: auto;
	}

	.author-post-image-image {
		width: 100%;
		height: auto;
	}

	.author-post-description {
		margin-top: 10px;
		padding-left: 0;
	}

	.author-post-link {
		display: block;
		margin-top: 10px;
		text-align: center;
	}

	.author-content-title-post {
		font-size: 18px;
	}

	.author-side-box {
		margin-left: 0;
		width: 100%;
		height: auto;
		padding: 20px;
		box-shadow: 0px 7px 20px 0px #0000001a;
	}

	.author-watchlist-information-title {
		padding-top: 0;
	}

	.author-watchlist-information-subtitle {
		font-size: 14px;
	}

	.author-watchlist-information-email-input {
		margin-bottom: 10px;
		padding: 8px;
	}

	.author-watchlist-information-subscribe-button {
		font-size: 14px;
	}

	.author-watchlist-information-subscribe-button::after {
		font-size: 24px;
		width: 20px;
		height: 14.14px;
		top: 3px;
	}

	.author-watchlist-information-description {
		font-size: 14px;
	}
}
@media (min-width: 769px) and (max-width: 1024px) {
	.author-latest-posts {
		margin-left: 20px !important;
		margin-right: 20px !important;
	}

	.author-side-box {
		grid-column: 2 / span 1;
		grid-row: 4 / span 2;
		margin-left: 0 !important;
		width: 100% !important;
		height: auto !important;
		padding: 20px !important;
		box-shadow: 0px 7px 20px 0px #0000001a !important;
	}

	.author-card-container {
		padding: 32px 20px;
	}

	.author-card {
		grid-template-columns: auto;
		grid-template-rows: auto auto auto;
		justify-items: center;
		text-align: center;
	}
}
@media (min-width: 1025px) and (max-width: 1400px) {
	.author-latest-posts {
		margin-left: 20px !important;
		margin-right: 20px !important;
	}

	.author-side-box {
		grid-column: 2 / span 1;
		grid-row: 4 / span 2;
		margin-left: 0 !important;
		width: 100% !important;
		height: auto !important;
		padding: 20px !important;
		box-shadow: 0px 7px 20px 0px #0000001a !important;
	}

	.author-post {
		flex: 1;
		margin: 0 10px;
	}

	.author-card-container {
		padding: 32px 20px;
	}

	.author-card {
		grid-template-columns: auto;
		grid-template-rows: auto auto auto;
		justify-items: center;
		text-align: center;
	}
}

#author-page .blog-container {
	overflow-x: visible !important;
	background-color: white !important;
}

.related-post-container {
	border: 2px solid #dfe1e7;
	border-radius: 24px;
	padding: 32px 12px 8px;
}

.bg-white {
	color: black;
}

.lead-capture {
	border-radius: 24px;
	border: 2px solid $slate-100;
	background: $white;
	padding: 32px 24px;
	display: flex;
	flex-direction: column;
	gap: 32px;
	@include breakpoint(lg) {
		top: 88px;
	}
}
</style>
